/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import filter from 'lodash/filter';
import get from 'lodash/get';
import {
    Select,
    Icon,
    FlexRow,
    FlexCol,
    Button,
    VisualOnlyLabel,
    NumberField,
    Heading,
    Spacer,
    RequiredValidation,
} from 'sarsaparilla';
import { dateSelectionProps } from 'ui-permit/src/props/multi-division/DateSelection';

const propTypes = {
    index: PropTypes.number.isRequired,
    selection: dateSelectionProps.isRequired,
    onChange: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
    divisions: PropTypes.shape({}),
    maxGroupSize: PropTypes.number,
    minGroupSize: PropTypes.number,
    hideGroupSize: PropTypes.bool,

    onFormValidate: PropTypes.func.isRequired,
    preferredDivisionLabel: PropTypes.string,
};

const defaultProps = {
    maxGroupSize: 10,
    minGroupSize: 0,
};

export default function PreferredDivisionGroupSizeItem(props) {
    const getDivisionOptions = (divisions) => {
        const filteredDivisions = filter(
            divisions,
            (div) => div.type !== 'Hidden' && !div.is_hidden
        );
        const value = Object.keys(filteredDivisions)
            .filter((key) => Object.hasOwn(filteredDivisions, key))
            .map((division) => ({
                label: filteredDivisions[division].name,
                value: filteredDivisions[division].id,
            }));
        value.unshift({ label: 'Select from list', value: '' });
        return value;
    };

    const handleChange = (valuePair) => {
        props.onChange({ ...props.selection, ...valuePair });
    };

    const renderDivisionSelect = (index) => {
        const validationPath = `division_id_${index}`;
        return (
            <FlexCol md={6}>
                <div className="rec-form-item-wrap">
                    <VisualOnlyLabel isRequired>
                        {props.preferredDivisionLabel}
                    </VisualOnlyLabel>
                    <Select
                        label={props.preferredDivisionLabel}
                        isLabelVisible={false}
                        isRequired
                        onChange={(event) =>
                            handleChange({
                                division_id: event.target.value,
                                preferred_date: null,
                            })
                        }
                        options={getDivisionOptions(props.divisions)}
                        value={props.selection.division_id}
                        id={`divisionSelect-${index}`}
                    />
                    <RequiredValidation
                        value={props.selection.division_id}
                        displayInSummary
                        onValidated={props.onFormValidate(validationPath)}
                        controlLabel={`Choice #${index + 1} Entrance`}
                    />
                </div>
            </FlexCol>
        );
    };

    const groupSizeSelect = (selectedGroupSize, maxGroupSize, index) => {
        const validationPath = `group_size_${index}`;
        return (
            <FlexCol md={3}>
                <div className="rec-form-item-wrap">
                    <VisualOnlyLabel htmlFor={`groupSize-${index}`} isRequired>
                        Group Size
                    </VisualOnlyLabel>
                    <NumberField
                        id={`groupSize-${index}`}
                        units="guests"
                        value={selectedGroupSize}
                        max={maxGroupSize}
                        onChange={(ct) =>
                            handleChange({ group_min_size: ct, group_max_size: ct })
                        }
                    />
                    <RequiredValidation
                        value={
                            props.selection.group_max_size === 0
                                ? ''
                                : props.selection.group_max_size.toString()
                        }
                        displayInSummary
                        onValidated={props.onFormValidate(validationPath)}
                        controlLabel={`Choice #${index + 1} Group Size`}
                    />
                </div>
            </FlexCol>
        );
    };

    const { index, divisions } = props;

    return (
        <div data-testid="preferred-dates-item" className="per-form-group-item">
            <FlexRow alignItems="end">
                <FlexCol md={11}>
                    <Heading headingLevel={4} appearance={'h6'}>
                        Choice #{index + 1}
                    </Heading>
                </FlexCol>
                <FlexCol md={1}>
                    <Button
                        appearance="subtle-danger"
                        id={`removeIcon-${index}`}
                        iconBeforeElement={<Icon iconName="delete" />}
                        onClick={() => props.onRemove(props.index)}
                        screenReaderTextBefore={`Remove choice ${
                            props.index + 1
                        } from the lottery preference list`}
                    />
                </FlexCol>
                <Spacer size={'md'} />
            </FlexRow>
            <FlexRow alignItems="end">
                {Object.keys(divisions).length > 1 ? renderDivisionSelect(index) : null}
                {!props.hideGroupSize
                    ? groupSizeSelect(
                          get(props, 'selection.group_max_size', 0),
                          props.maxGroupSize,
                          index
                      )
                    : null}
            </FlexRow>
        </div>
    );
}

PreferredDivisionGroupSizeItem.propTypes = propTypes;
PreferredDivisionGroupSizeItem.defaultProps = defaultProps;
